import { useDispatch, useSelector } from 'react-redux';
import { TabContent, TabPane } from 'reactstrap';
import { Trans, useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { isMobile } from 'utils/deviceHelper';
import { INITIAL_STATE } from 'store/smsDashboard';
import Conversation from 'components/molecules/smsDashboard/conversation';
import InfiniteScroll from 'components/atoms/infinityScroll';
import MessagesSearchIcon from 'components/atoms/icons/messagesSearchEmptyIcon';
import ConversationsEmptyIcon from 'components/atoms/icons/conversationsEmptyIcon';
import { useSmsDashboardConversations } from 'infrastructure/hooks/smsDashboard/useConversations';
import BaseLoader from 'components/atoms/base-loader';
import React, { useMemo } from 'react';
import { useMarkConversation } from 'infrastructure/hooks/smsDashboard/useMarkConversation';
import { useSmsDashboardConversationsByKeyword } from 'infrastructure/hooks/smsDashboard/useConversationsByKeyword';

import s from './styles.module.scss';

import type {
  IConversation,
  IConversationsListRes,
} from 'infrastructure/interfaces';
import type { RootDispatch, RootState } from 'store';

interface Props {
  selectMode: boolean;
  checkedConversations: any[];
  setCheckedConversations: React.Dispatch<React.SetStateAction<number[]>>;
  setScrollToMessage: React.Dispatch<React.SetStateAction<any>>;
}

const formatLastMessage = (message: string, isMobileView: boolean) => {
  if (!message || message.length === 0) {
    return i18n.t('smsDashboard.no_messages_yet');
  }
  if (isMobileView) {
    const mobileMessagePreviewLength = 35;
    return message.length <= mobileMessagePreviewLength
      ? message
      : `${message.substr(0, mobileMessagePreviewLength - 4)} ...`;
  }
  return message;
};

const ConversationsList: React.FC<Props> = ({
  selectMode,
  checkedConversations,
  setCheckedConversations,
  setScrollToMessage,
}) => {
  const { t } = useTranslation();
  const {
    smsDashboard: { changeCurrentConversation },
  } = useDispatch<RootDispatch>();

  const device = useSelector((state: RootState) => state.user.deviceType);
  const deviceIsMobile: boolean = isMobile(device);

  const { conversationType, conversationTypes, currentConversation, filters } =
    useSelector((state: RootState) => state.smsDashboard);

  const memoizedDate = useMemo(() => {
    const currentDate = new Date();
    return currentDate;
  }, [
    conversationType,
    filters.closed,
    filters.read,
    filters.lastMessageDate,
    filters.searchQuery,
  ]);

  const isFiltersDefaultState =
    filters.read === INITIAL_STATE.filters.read &&
    filters.closed === INITIAL_STATE.filters.closed &&
    filters.lastMessageDate === INITIAL_STATE.filters.lastMessageDate &&
    filters.searchQuery === INITIAL_STATE.filters.searchQuery;

  const {
    conversations,
    conversationsLoading,
    fetchNextPage,
    isError,
    isLoading,
  } = useSmsDashboardConversations({
    cursor: memoizedDate,
    type: conversationType,
    read: filters.read,
    closed: filters.closed,
    assignedToMe: filters.assignedToMe,
    lastMessageDate: filters.lastMessageDate,
    searchQuery: filters.searchQuery,
  });

  const {
    conversationsByKeyword,
    conversationsByKeywordTotalCount,
    conversationsByKeywordLoading,
    fetchNextPageConversationsByKeyword,
    isErrorConversationsByKeyword,
  } = useSmsDashboardConversationsByKeyword({
    cursor: memoizedDate,
    type: conversationType,
    read: filters.read,
    closed: filters.closed,
    lastMessageDate: filters.lastMessageDate,
    searchQuery: filters.searchQuery,
  });

  const { markConversation } = useMarkConversation({});

  const onClickConversation = (conversationProp: IConversation) => {
    changeCurrentConversation(conversationProp);
    if (conversationProp && conversationProp.read === false) {
      markConversation({ conversationId: conversationProp.id, read: true });
    }
  };

  const onClickConversationByKeyword = (conversationProp: IConversation) => {
    changeCurrentConversation(conversationProp);
    setScrollToMessage({
      createdAt: conversationProp.lastMessageDate,
      highlightSearchQuery: filters.searchQuery,
      action: '',
    });
  };

  const conversationListMessage = isFiltersDefaultState ? (
    <div className={s['empty-message']}>
      <ConversationsEmptyIcon />
      <div className={s.message}>
        <Trans
          i18nKey="smsDashboard.empty_conversation_message3"
          values={{
            description1: t('smsDashboard.unread_messages'),
            description2: t('smsDashboard.default_filter'),
            description3: t('smsDashboard.change_the_filter_to_all'),
          }}
          components={{
            primary: <span />,
            secondary: <span />,
            third: <span />,
          }}
        />
      </div>
    </div>
  ) : (
    <div className={s['empty-message']}>
      <MessagesSearchIcon />
      <div className={s.message}>
        <Trans
          i18nKey="smsDashboard.empty_conversation_message2"
          values={{
            description1: `${t('smsDashboard.no_result_found')},`,
            description2: t('smsDashboard.change_filter'),
          }}
          components={{
            primary: <span />,
            secondary: <span />,
          }}
        />
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className={s['empty-message']}>
        <BaseLoader
          style={{ textAlign: 'center', padding: '1rem' }}
          height={30}
          width={30}
          loading
        />
      </div>
    );
  }

  return (
    <TabContent
      activeTab={conversationType}
      className={s['conversations-messages']}
    >
      {conversationTypes.map((type: string) => {
        return (
          <TabPane
            tabId={type}
            key={type}
            hidden={conversationType !== type}
            className={s['tab-pane-class-name']}
            style={{ display: 'flex' }}
          >
            {filters.searchQuery && (
              <div className={s['search-seperator']}>
                {t('labels.contacts')} (
                {conversations?.reduce(
                  (acc, conversationData) =>
                    acc +
                    (Array.isArray(conversationData?.items)
                      ? conversationData.items.length
                      : 0),
                  0,
                )}
                )
              </div>
            )}

            {conversations?.map(
              (conversationData: IConversationsListRes | undefined) => {
                return conversationData?.items.map((conversation) => (
                  <Conversation
                    key={`conversation-${conversation.id}-${conversation.lastMessageDate}`}
                    id={conversation.id}
                    assignedByFullname={conversation.assignedByFullname}
                    assignedAt={conversation.assignedAt}
                    selected={
                      (currentConversation as unknown as IConversation)?.id ===
                      conversation.id
                    }
                    patientName={
                      conversation.name ||
                      conversation.patient?.fullNameWithOnlyTitle ||
                      conversation.patient?.patientPhone
                    }
                    lastMessage={formatLastMessage(
                      conversation.lastMessage,
                      deviceIsMobile,
                    )}
                    lastMessageDate={conversation.lastMessageDate}
                    unreadMessageCount={conversation.unreadMessageCount}
                    hasPatientMessage={conversation.hasPatientMessage}
                    isRead={conversation.read}
                    closed={conversation.closed}
                    spam={conversation.spam}
                    onClick={() => onClickConversation(conversation)}
                    selectMode={selectMode}
                    checkedConversations={checkedConversations}
                    setCheckedConversations={setCheckedConversations}
                  />
                ));
              },
            )}
            {filters.searchQuery && (
              <>
                <div className={s['search-seperator']}>
                  {t('smsDashboard.messages')} (
                  {conversationsByKeywordTotalCount})
                </div>
                {conversationsByKeyword?.map(
                  (conversationData: IConversationsListRes | undefined) => {
                    return conversationData?.items.map((conversation) => (
                      <Conversation
                        key={`conversation-${conversation.id}-${conversation.lastMessageDate}`}
                        id={conversation.id}
                        assignedByFullname={conversation.assignedByFullname}
                        assignedAt={conversation.assignedAt}
                        selected={
                          (currentConversation as unknown as IConversation)
                            ?.id === conversation.id
                        }
                        patientName={
                          conversation.name ||
                          conversation.patient?.fullNameWithOnlyTitle ||
                          conversation.patient?.patientPhone
                        }
                        lastMessage={formatLastMessage(
                          conversation.lastMessage,
                          deviceIsMobile,
                        )}
                        lastMessageDate={conversation.lastMessageDate}
                        unreadMessageCount={conversation.unreadMessageCount}
                        hasPatientMessage={conversation.hasPatientMessage}
                        isRead={conversation.read}
                        closed={conversation.closed}
                        spam={conversation.spam}
                        onClick={() =>
                          onClickConversationByKeyword(conversation)
                        }
                        selectMode={selectMode}
                        checkedConversations={checkedConversations}
                        setCheckedConversations={setCheckedConversations}
                      />
                    ));
                  },
                )}
              </>
            )}
            {!filters.searchQuery && conversations?.[0]?.items.length === 0 && (
              <div className={s['empty-conversations']}>
                {conversationListMessage}
              </div>
            )}

            {filters.searchQuery &&
              conversations?.[0]?.items.length === 0 &&
              conversationsByKeywordTotalCount === 0 && (
                <div className={s['empty-conversations']}>
                  {conversationListMessage}
                </div>
              )}
            <InfiniteScroll
              loadMore={
                filters.searchQuery
                  ? fetchNextPageConversationsByKeyword
                  : fetchNextPage
              }
              disabled={
                filters.searchQuery
                  ? conversationsByKeywordLoading
                  : conversationsLoading
              }
              allLoaded={
                filters.searchQuery
                  ? conversationsByKeyword === undefined ||
                    !conversationsByKeyword[conversationsByKeyword.length - 1]
                      ?.hasMore
                  : conversations === undefined ||
                    !conversations[conversations.length - 1]?.hasMore
              }
              uniqueId={`conversations-list-${conversationType}`}
              manualMode={
                isError ||
                isErrorConversationsByKeyword ||
                conversationType !== type
              }
            />
          </TabPane>
        );
      })}
    </TabContent>
  );
};

export default ConversationsList;
